import { useState, useRef } from 'react';
import { Group, Button, Text, Image, Stack, Box, CloseButton, Loader } from '@mantine/core';
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { IconUpload, IconPhoto, IconX } from '@tabler/icons-react';
import { validateImageFile } from '@/lib/storage';

interface ImageUploadProps {
  value?: string | null;
  onChange: (file: File | null, previewUrl?: string) => void;
  onRemove?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export function ImageUpload({ value, onChange, onRemove, disabled, loading }: ImageUploadProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);
  const openRef = useRef<() => void>(null);

  const handleDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    onChange(file, url);
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onChange(null);
    onRemove?.();
  };

  if (previewUrl) {
    return (
      <Box pos="relative" style={{ maxWidth: 300 }}>
        <Image
          src={previewUrl}
          alt="Asset photo"
          radius="md"
          fit="contain"
          h={200}
          style={{ border: '1px solid #dee2e6' }}
        />
        {!disabled && (
          <CloseButton
            pos="absolute"
            top={8}
            right={8}
            size="lg"
            radius="xl"
            variant="filled"
            onClick={handleRemove}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
          />
        )}
        {loading && (
          <Box
            pos="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            <Loader size="lg" />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Dropzone
      openRef={openRef}
      onDrop={handleDrop}
      accept={IMAGE_MIME_TYPE}
      maxSize={5 * 1024 * 1024}
      maxFiles={1}
      disabled={disabled}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <Group justify="center" gap="xl" mih={120} style={{ pointerEvents: 'none' }}>
        <Dropzone.Accept>
          <IconUpload size={52} stroke={1.5} />
        </Dropzone.Accept>
        <Dropzone.Reject>
          <IconX size={52} stroke={1.5} />
        </Dropzone.Reject>
        <Dropzone.Idle>
          <IconPhoto size={52} stroke={1.5} />
        </Dropzone.Idle>

        <Stack gap="xs" align="center">
          <Text size="xl" inline>
            Drag image here or click to select
          </Text>
          <Text size="sm" c="dimmed" inline mt={7}>
            Max file size: 5MB. Supported formats: JPEG, PNG, GIF, WebP
          </Text>
        </Stack>
      </Group>
    </Dropzone>
  );
}
